// Admin QC tool: paste a case ID or MDL order ID and see every case
// image with its original / MIO output side-by-side. Lets QC review
// enhanced output without engineering having to query the DB.

import { useCallback, useMemo, useRef, useState, type FormEvent } from 'react'
import { Redirect } from 'react-router-dom'
import { useRecoilValue } from 'recoil'
import styled from 'styled-components'
import colors from 'utils/styling/colors'
import { userAtom } from '@/atoms/_globalAtoms'
import { isSuperAdmin } from '@/hooks/utility/useIsSuperAdmin'
import { Navigation } from 'components'
import { Button } from '@/components/priority'
import Flex from '@/components/priority/Flex/Flex'
import Text from '@/components/priority/Text/Text'
import {
  fetchCaseImageQc,
  type CaseImageQcResponse,
  type CaseImageQcRow,
  type CaseImageEdits,
  type CaseImageQcLookupKind,
} from '@/api/case-image-admin'

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

const NOOP_THRESHOLD = 0.01

// data-testid registry — mirrored in apps/mdl-frontend/tests/utils/selectors.ts
// under TEST_IDS.CASE_IMAGE_QC so E2E tests can target these without
// inline string selectors. Source of truth for the strings lives there;
// these are local constants for the component (same pattern as PdfJobsPage).
const TID_PAGE = 'case-image-qc-page'
const TID_INPUT = 'case-image-qc-input'
const TID_SUBMIT = 'case-image-qc-submit'
const TID_ERROR = 'case-image-qc-error'
const TID_ROW_PREFIX = 'case-image-qc-row-'

// All page chrome is keyed to the styled-components theme so it inherits
// MDL's light/dark/high-contrast palettes. See utils/styling/palettes.ts
// for what each key resolves to per mode.

const Container = styled.div`
  padding: 24px 32px;
  max-width: 1400px;
  margin: 0 auto;
  color: ${colors('gray1')};
`

const HeaderBlock = styled.div`
  margin-bottom: 20px;
`

const PageTitle = styled.h1`
  font-size: 28px;
  font-weight: 600;
  margin: 0 0 6px;
  color: ${colors('almostBlack')};
`

const PageSubtitle = styled.p`
  font-size: 14px;
  color: ${colors('gray2')};
  margin: 0;
  max-width: 760px;
`

const Form = styled.form`
  display: flex;
  gap: 12px;
  align-items: center;
  margin: 16px 0 24px;
`

const Input = styled.input`
  flex: 1;
  padding: 10px 12px;
  border: 1px solid ${colors('gray4')};
  border-radius: 6px;
  background: ${colors('white')};
  color: ${colors('gray1')};
  font-family: monospace;
  font-size: 13px;
  &::placeholder {
    color: ${colors('gray3')};
  }
`

const KindToggle = styled.div`
  display: inline-flex;
  border: 1px solid ${colors('gray4')};
  border-radius: 6px;
  overflow: hidden;
`

const KindButton = styled.button<{ active: boolean }>`
  padding: 8px 14px;
  background: ${(p) => (p.active ? colors('brand') : colors('white'))};
  color: ${(p) => (p.active ? colors('white') : colors('gray1'))};
  border: none;
  cursor: pointer;
  font-size: 13px;
  font-weight: ${(p) => (p.active ? 600 : 400)};
`

const Banner = styled.div<{ tone: 'info' | 'warn' | 'error' }>`
  padding: 12px 16px;
  border-radius: 6px;
  margin-bottom: 16px;
  font-size: 14px;
  background: ${(p) =>
    p.tone === 'error'
      ? colors('error', 15)
      : p.tone === 'warn'
        ? colors('orange', 15)
        : colors('palePrimary')};
  color: ${(p) =>
    p.tone === 'error'
      ? colors('error')
      : p.tone === 'warn'
        ? colors('orange')
        : colors('gray1')};
  border: 1px solid
    ${(p) =>
      p.tone === 'error'
        ? colors('error', 30)
        : p.tone === 'warn'
          ? colors('orange', 30)
          : colors('gray4')};
`

const CaseCard = styled.div`
  padding: 16px 20px;
  border: 1px solid ${colors('gray4')};
  border-radius: 8px;
  background: ${colors('palePrimary')};
  margin-bottom: 20px;
`

const Row = styled.div`
  border: 1px solid ${colors('gray4')};
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  background: ${colors('white')};
`

const RowHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  font-size: 13px;
  color: ${colors('gray2')};
`

// Two columns (Original + MIO). No Pintura pane: CaseImage.pinturaUrl/
// pinturaEdits are never written by the pipeline yet (see QcImage in
// qc.service.ts), so a third cell would be empty for every image.
const ImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
`

const ImageCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 12px;
  color: ${colors('gray1')};
`

const CellLabel = styled.div`
  font-weight: 600;
  color: ${colors('almostBlack')};
`

// Real <img> + loading="lazy" so the browser only fetches thumbs the
// admin actually scrolls to — keeps 100+ image cases (memorials, family
// uploads) from saturating the connection pool. Anchor wraps the image
// so click-to-full-size still works.
const ThumbAnchor = styled.a`
  display: block;
  width: 100%;
  aspect-ratio: 4 / 3;
  border: 1px solid ${colors('gray4')};
  border-radius: 6px;
  overflow: hidden;
  background: ${colors('gray5')};
`

const Thumb = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
`

const ImageThumbEmpty = styled.div`
  display: grid;
  place-items: center;
  width: 100%;
  aspect-ratio: 4 / 3;
  background: ${colors('gray5')};
  border: 1px dashed ${colors('gray4')};
  border-radius: 6px;
  color: ${colors('gray3')};
  font-size: 12px;
`

// Code blocks stay a fixed near-black across modes — they read as
// "code", not "content", and the contrast against a darker page is
// fine because the typography sets them apart.
const EditsJson = styled.pre`
  background: #1e1e1e;
  color: #d4d4d4;
  padding: 10px 12px;
  border-radius: 6px;
  font-size: 11px;
  white-space: pre-wrap;
  word-break: break-all;
  margin: 0;
`

const ScoreBadge = styled.span<{ noop: boolean }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  background: ${(p) => (p.noop ? colors('orange', 15) : colors('brand', 15))};
  color: ${(p) => (p.noop ? colors('orange') : colors('brand'))};
  border: 1px solid
    ${(p) => (p.noop ? colors('orange', 30) : colors('brand', 30))};
`

const MetaText = styled.span`
  color: ${colors('gray2')};
`

const SubtleText = styled.span`
  color: ${colors('gray3')};
  font-size: 11px;
`

const IdText = styled.span`
  color: ${colors('gray2')};
  font-size: 12px;
  font-family: monospace;
`

const formatEditsJson = (edits: CaseImageEdits): string => {
  if (!edits) return '(none)'
  const ordered = Object.fromEntries(
    Object.entries(edits).map(([k, v]) => [k, Number(v.toFixed(3))]),
  )
  return JSON.stringify(ordered, null, 2)
}

const renderThumb = (label: string, url: string | null) =>
  url ? (
    <ThumbAnchor
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`${label} (open full size)`}
    >
      <Thumb src={url} alt={label} loading="lazy" />
    </ThumbAnchor>
  ) : (
    <ImageThumbEmpty>no {label.toLowerCase()}</ImageThumbEmpty>
  )

const ImageRow = ({ image }: { image: CaseImageQcRow }) => {
  const score = image.mioChangeScore
  const isNoop = score !== null && score < NOOP_THRESHOLD
  const dimensions =
    image.width && image.height ? `${image.width}×${image.height}` : 'unknown'
  const sizeKb =
    image.fileSize !== null ? `${Math.round(image.fileSize / 1024)} KB` : '—'
  return (
    <Row data-testid={`${TID_ROW_PREFIX}${image.id}`}>
      <RowHeader>
        <div>
          <Text as="strong" color="gray1" weight="600">
            #{image.sortOrder}
          </Text>{' '}
          {image.default && (
            <Text as="span" color="orange" weight="600">
              default{' '}
            </Text>
          )}
          <MetaText>
            · {dimensions} · {sizeKb} · uploaded{' '}
            {image.uploadSource ?? 'unknown'}
            {image.latestJobStatus &&
              ` · job ${image.latestJobStatus.toLowerCase()}`}
          </MetaText>
        </div>
        {score !== null && (
          <ScoreBadge noop={isNoop} title="Sum of |delta| from neutral">
            score {score.toFixed(3)}
            {isNoop && ' — no visible change'}
          </ScoreBadge>
        )}
      </RowHeader>
      <ImageGrid>
        <ImageCell>
          <CellLabel>Original</CellLabel>
          {renderThumb('Original', image.originalUrl)}
          <SubtleText>{image.originalFileName ?? '—'}</SubtleText>
        </ImageCell>
        <ImageCell>
          <CellLabel>MIO enhanced</CellLabel>
          {renderThumb('MIO', image.mioUrl)}
          <EditsJson>{formatEditsJson(image.mioEdits)}</EditsJson>
        </ImageCell>
      </ImageGrid>
      {(image.error || image.latestJobError) && (
        <Banner tone="error" style={{ marginTop: 12, marginBottom: 0 }}>
          {image.error && <div>image.error: {image.error}</div>}
          {image.latestJobError && (
            <div>job.errorMessage: {image.latestJobError}</div>
          )}
        </Banner>
      )}
    </Row>
  )
}

// Local axios-error type guard using the marker property
// (`isAxiosError === true`), the documented runtime signal across all
// 0.x and 1.x axios lines, so this file does not depend on the resolved
// axios version.
const isAxiosErrorShape = (
  err: unknown,
): err is { response?: { data?: { error?: unknown } }; message?: string } =>
  typeof err === 'object' &&
  err !== null &&
  (err as { isAxiosError?: unknown }).isAxiosError === true

const extractErrorMessage = (err: unknown): string => {
  if (isAxiosErrorShape(err)) {
    const data = err.response?.data
    if (data && typeof data.error === 'string') return data.error
    if (typeof err.message === 'string' && err.message) return err.message
  }
  if (err instanceof Error && err.message) return err.message
  return 'Lookup failed.'
}

export const CaseImageQc = () => {
  const user = useRecoilValue(userAtom)
  // Backend gate is auth('superAdmin'); useIsAdmin is broader (includes
  // corporationAdmin and actAsAdmin) which would let those users land on
  // a page that 401s on submit. Match the backend exactly.
  const superAdmin = isSuperAdmin(user)

  const [kind, setKind] = useState<CaseImageQcLookupKind>('orderId')
  const [rawId, setRawId] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [data, setData] = useState<CaseImageQcResponse | null>(null)

  // In-flight token: if a second submit fires (e.g., toggling kind +
  // pressing Enter before the disabled state takes hold), only the
  // latest request's response is allowed to update state. Without this,
  // a slow first lookup can resolve after a fast second one and
  // overwrite the visible result with stale data — a real risk because
  // the admin would have no on-screen cue that what they see does not
  // match what they typed.
  const requestTokenRef = useRef(0)

  const trimmed = rawId.trim()
  const isValid = useMemo(() => UUID_RE.test(trimmed), [trimmed])

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault()
      if (!isValid) {
        setError('Paste a valid UUID.')
        return
      }
      const token = ++requestTokenRef.current
      setLoading(true)
      setError(null)
      setData(null)
      try {
        const res = await fetchCaseImageQc(kind, trimmed)
        if (token !== requestTokenRef.current) return
        setData(res.data)
      } catch (err) {
        if (token !== requestTokenRef.current) return
        setError(extractErrorMessage(err))
      } finally {
        if (token === requestTokenRef.current) setLoading(false)
      }
    },
    [isValid, kind, trimmed],
  )

  // userAtom is filled in after the /v1/user request resolves, so on a hard
  // load (a reload, a bookmark, a link opened in a new tab) the first render
  // has no user yet. `null` means "still loading", not "not a super admin":
  // render nothing until the record arrives, or the redirect below would send
  // a real super admin away before their record loads. A session whose record
  // never loads sees an empty page.
  if (user === null) return null
  if (!superAdmin) return <Redirect to="/dashboard" />

  const noopCount = data
    ? data.images.filter(
        (img) =>
          img.mioChangeScore !== null && img.mioChangeScore < NOOP_THRESHOLD,
      ).length
    : 0

  return (
    <>
      <Navigation type="admin" />
      <Container data-testid={TID_PAGE}>
        <HeaderBlock>
          <PageTitle>Case Image QC</PageTitle>
          <PageSubtitle>
            Inspect original and MIO enhanced output for every image on a case.
            Use this to verify MIO is producing visible changes before going
            back to QC with &ldquo;no change&rdquo; reports.
          </PageSubtitle>
        </HeaderBlock>

        <Form onSubmit={handleSubmit}>
          <KindToggle role="group" aria-label="Lookup type">
            <KindButton
              type="button"
              active={kind === 'orderId'}
              aria-pressed={kind === 'orderId'}
              onClick={() => setKind('orderId')}
            >
              Order ID
            </KindButton>
            <KindButton
              type="button"
              active={kind === 'caseId'}
              aria-pressed={kind === 'caseId'}
              onClick={() => setKind('caseId')}
            >
              Case ID
            </KindButton>
          </KindToggle>
          <Input
            placeholder="Paste a UUID"
            value={rawId}
            onChange={(e) => setRawId(e.target.value)}
            data-testid={TID_INPUT}
            spellCheck={false}
          />
          <Button
            type="submit"
            disabled={!isValid || loading}
            data-testid={TID_SUBMIT}
          >
            {loading ? 'Loading…' : 'Look up'}
          </Button>
        </Form>

        {error && (
          <Banner tone="error" data-testid={TID_ERROR}>
            {error}
          </Banner>
        )}

        {data && (
          <>
            <CaseCard>
              <Flex justify="space-between" align="center">
                <div>
                  <Text weight="600" color="almostBlack">
                    {data.case.nameOfDeceased ?? '(no deceased name)'}
                  </Text>
                  <Text color="gray2" size="13px">
                    {data.case.funeralHomeName ?? 'unknown funeral home'}
                  </Text>
                </div>
                <IdText>
                  case {data.case.id}
                  {data.orderId && (
                    <>
                      <br />
                      order {data.orderId}
                    </>
                  )}
                </IdText>
              </Flex>
            </CaseCard>

            {data.images.length === 0 ? (
              <Banner tone="info">No case_image rows for this case.</Banner>
            ) : (
              <>
                <Banner tone={noopCount > 0 ? 'warn' : 'info'}>
                  {data.images.length} image
                  {data.images.length === 1 ? '' : 's'}
                  {noopCount > 0 ? (
                    <>
                      {' — '}
                      <strong>{noopCount}</strong> with MIO change score &lt;{' '}
                      {NOOP_THRESHOLD} (visually no-op).
                    </>
                  ) : null}
                </Banner>
                {data.images.map((img) => (
                  <ImageRow key={img.id} image={img} />
                ))}
              </>
            )}
          </>
        )}
      </Container>
    </>
  )
}

export default CaseImageQc
